import { HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { AuthenticationService } from '../authentication.service';
import { tap, catchError, filter, take, switchMap } from 'rxjs/operators';
import { Token } from 'src/app/shared/models/token';
import { AlertService } from '../services/alert.service';
import { HttpErrorHandlerService } from '../services/http-error-handler.service';
import { environment } from 'src/environments/environment';
import { APIS } from '../enums/apis';

/** Origins for Simfinix API bases (environment.api*), used for cookie sessions (withCredentials). */
let cachedApiOrigins: Set<string> | null = null;

function getApiOrigins(): Set<string> {
   if (cachedApiOrigins) {
      return cachedApiOrigins;
   }
   const set = new Set<string>();
   const env = environment as Record<string, unknown>;
   for (const key of Object.keys(env)) {
      if (!key.startsWith('api')) {
         continue;
      }
      const v = env[key];
      if (typeof v !== 'string' || !v.startsWith('http')) {
         continue;
      }
      try {
         set.add(new URL(v).origin);
      } catch {
         /* ignore invalid URL */
      }
   }
   cachedApiOrigins = set;
   return set;
}

function isApiOriginUrl(url: string): boolean {
   try {
      return getApiOrigins().has(new URL(url).origin);
   } catch {
      return false;
   }
}

/** withCredentials para mismo origen vía proxy local (/local-proxy/* en environment.local.ts). */
function isLocalDevProxyUrl(url: string): boolean {
   const paths = (environment as { localDevProxyPaths?: string[] }).localDevProxyPaths;
   if (!paths?.length) {
      return false;
   }
   let pathname: string;
   try {
      pathname = url.startsWith('http') ? new URL(url).pathname : url.split(/[?#]/)[0];
   } catch {
      return false;
   }
   return paths.some((p) => pathname.startsWith(p));
}

function shouldSendCredentials(url: string): boolean {
   return isApiOriginUrl(url) || isLocalDevProxyUrl(url);
}

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
   private isRefreshing = false;
   private refreshTokenSubject = new BehaviorSubject<string | null>(null);

   constructor(
      private authService: AuthenticationService,
      private alertService: AlertService,
      private handlerErrorService: HttpErrorHandlerService,
      private http: HttpClient,
   ) {}

   private isAuthRefreshRequest(req: HttpRequest<any>): boolean {
      const path = req.url.split(/[?#]/)[0];
      return path.endsWith(APIS.REFRESH);
   }

   /** Do not attempt token refresh on failed login (401 bad credentials). */
   private isAuthLoginRequest(req: HttpRequest<any>): boolean {
      const path = req.url.split(/[?#]/)[0];
      // return path.endsWith(APIS.LOGIN_V2) || path.endsWith(APIS.LOGIN);
      return path.endsWith(APIS.LOGIN_V2) ||
      path.endsWith(APIS.LOGIN) ||
      path.endsWith(APIS.LOGIN_V3);

   }

   private ensureApiCredentials(req: HttpRequest<any>): HttpRequest<any> {
      if (!shouldSendCredentials(req.url) || req.withCredentials === true) {
         return req;
      }
      return req.clone({ withCredentials: true });
   }

   intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      let req = this.ensureApiCredentials(request);
      return next.handle(req).pipe(
         tap((evt) => {
            if (evt instanceof HttpResponse) {
               const accessToken = evt.headers.get(environment.accessTokenHeaderName) ?? null;
               if (accessToken) {
                  const token: Token = {
                     accessToken,
                     refreshToken: this.authService.userValue?.token?.refreshToken ?? null,
                  };
                  this.authService.updateCurrentUser({ token });
               }
            }
         }),
         catchError((err: any) => {
            if (err instanceof HttpErrorResponse) {
               if (err.status === 401 && !this.isAuthRefreshRequest(request) && !this.isAuthLoginRequest(request)) {
                  return this.handle401Error(request, next);
               }

               // Si es la request de refresh del bootstrap (primer ingreso sin sesión), no mostrar alerta
               if (!this.isAuthRefreshRequest(request)) {
                  try {
                     const messageError = this.handlerErrorService.handleError(err);
                     if (messageError) {
                        this.alertService.openError(messageError);
                     }
                  } catch (e: any) {
                     if (e.status != 428) {
                        this.alertService.openError('Ha ocurrido un error');
                     }
                  }
               }
            }
            return throwError(() => err);
         }),
      );
   }

   private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      if (this.isRefreshing) {
         return this.refreshTokenSubject.pipe(
            filter((token): token is string => token != null),
            take(1),
            switchMap(() => next.handle(this.ensureApiCredentials(request))),
         );
      }

      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshBody = { applicationId: this.authService.getAuthApplicationId() };

      return this.http
         .post(`${environment.apiAuthService}${APIS.REFRESH}`, refreshBody, {
            withCredentials: true,
            responseType: 'text',
         })
         .pipe(
            switchMap((newAccessToken: string) => {
               this.isRefreshing = false;
               this.refreshTokenSubject.next(newAccessToken);
               const currentUser = this.authService.userValue;
               this.authService.updateCurrentUser({
                  token: { accessToken: newAccessToken, refreshToken: currentUser?.token?.refreshToken ?? null },
               });
               return next.handle(this.ensureApiCredentials(request));
            }),
            catchError((refreshError) => {
               this.isRefreshing = false;
               try {
                  const res =
                     refreshError instanceof HttpErrorResponse
                        ? refreshError
                        : new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' });
                  const msg = this.handlerErrorService.handleError(res);
                  if (msg) {
                     this.alertService.openError(msg);
                  }
               } catch (e: any) {
                  if (e?.status != 428) {
                     this.alertService.openError('Ha ocurrido un error');
                  }
               }
               const dead = this.refreshTokenSubject;
               this.refreshTokenSubject = new BehaviorSubject<string | null>(null);
               dead.error(refreshError);
               return throwError(() => refreshError);
            }),
         );
   }
}
